import { useEffect } from 'react'
import { SITE } from '@shared/config'

const ORIGIN = SITE.origin

/** Keep the site name when it fits a conventional search-result title, while
 * preserving a descriptive article title instead of truncating its topic. */
export function withSiteName(title: string, maxLength = 60): string {
  const branded = `${title} | ${SITE.name}`
  return branded.length <= maxLength ? branded : title
}

interface SeoInput {
  title: string
  description?: string
  /** Absolute path beginning with "/" (e.g. "/2026/07/22/my-post/"). */
  path: string
  image?: string
  type?: 'website' | 'article'
  robots?: string
  canonical?: boolean
  alternates?: Array<{ lang: string; path: string }>
  lang?: string
}

function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function removeMeta(selector: string) {
  document.head.querySelector(selector)?.remove()
}

function setAlternateLinks(alternates: Array<{ lang: string; path: string }> = []) {
  // Own EVERY hreflang link, including the prerendered ones (they carry no
  // marker attribute) — otherwise SPA navigation duplicates the set and leaves
  // the first page's alternates behind.
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => link.remove())
  for (const alternate of alternates) {
    const link = document.createElement('link')
    link.rel = 'alternate'
    link.hreflang = alternate.lang
    link.href = alternate.path.startsWith('http') ? alternate.path : `${ORIGIN}${alternate.path}`
    document.head.appendChild(link)
  }
}

const OG_LOCALE: Record<string, string> = { en: 'en_US', fr: 'fr_FR', es: 'es_ES' }

/**
 * Keep <head> in sync during client-side navigation. The build-time prerender
 * (scripts/seo-build.mjs) is what crawlers read; this keeps title/canonical/
 * social tags correct for users who share a live URL.
 */
export function useSeo({
  title,
  description,
  path,
  image,
  type = 'website',
  robots = 'index, follow, max-image-preview:large, max-snippet:-1',
  canonical = true,
  alternates = [],
  lang = 'en',
}: SeoInput) {
  useEffect(() => {
    const url = `${ORIGIN}${path}`
    document.documentElement.lang = lang
    document.title = title
    if (description) setMeta('meta[name="description"]', 'name', 'description', description)
    else removeMeta('meta[name="description"]')
    setMeta('meta[name="robots"]', 'name', 'robots', robots)
    if (canonical) setLink('canonical', url)
    else document.head.querySelector('link[rel="canonical"]')?.remove()
    setAlternateLinks(alternates)

    setMeta('meta[property="og:title"]', 'property', 'og:title', title)
    setMeta('meta[property="og:type"]', 'property', 'og:type', type)
    setMeta('meta[property="og:url"]', 'property', 'og:url', url)
    setMeta('meta[property="og:locale"]', 'property', 'og:locale', OG_LOCALE[lang] ?? OG_LOCALE.en)
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title)
    if (image) {
      const img = image.startsWith('http') ? image : `${ORIGIN}${image}`
      setMeta('meta[property="og:image"]', 'property', 'og:image', img)
      setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', img)
    } else {
      removeMeta('meta[property="og:image"]')
      removeMeta('meta[name="twitter:image"]')
    }
    if (description) {
      setMeta('meta[property="og:description"]', 'property', 'og:description', description)
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description)
    } else {
      removeMeta('meta[property="og:description"]')
      removeMeta('meta[name="twitter:description"]')
    }
    // page_view is emitted centrally by AnalyticsTracker on route change.
  }, [title, description, path, image, type, robots, canonical, alternates, lang])
}
